import { useEffect, useMemo, useState } from 'react'
import { get } from '../../http/restservice'
import { useNavigate } from 'react-router-dom'
import env from '../../env/env'
import { toast } from 'sonner'
import { AuthFloatingInput, AuthFloatingPhoneInput } from '../../Components/Auth/AuthFloatingInput'
import { darkGridStyle, lightGridStyle } from '../../Components/Auth/AuthCarousel'
import { Button } from '../../Components/ui/button'
import { useThemeMode } from '../../Provider/ThemeContextProvider'
import { cn } from '../../lib/utils'

const ADMIN_ADD_PLAN_PATH =
  '/6fb2a4ac30ddccb8b418e27864a778dee5409a7da4c55660f49f21b8543289b4/admin/addPlan'

const normalizePhoneNumber = (value) => {
  if (!value) return ''
  return value.startsWith('+91') ? value : `+91${String(value).replace(/^\+91/, '')}`
}

const fetchCustomerDetails = async (phoneNumber) => {
  const encodedPhone = encodeURIComponent(phoneNumber)
  const url = `${env.API_URL}customerservice/api/customer/details/${encodedPhone}`

  try {
    const response = await get(url)
    if (!response || response.isError) {
      return response
    }

    // Keep the same session/nav shape AddPlan and holdings already expect: { data: customer }
    const normalized = {
      code: response.code,
      status:
        response.code === '200' && String(response.customerFound) === 'true' ? 'SUCCESS' : 'FAILED',
      data: response.customerDetails,
      customerFound: response.customerFound,
    }

    if (normalized.status === 'SUCCESS' && normalized.data) {
      if (!normalized.data.phoneNumber && phoneNumber) {
        normalized.data.phoneNumber = phoneNumber
      }
      sessionStorage.setItem('actionPlanUser', JSON.stringify(normalized))
      sessionStorage.setItem('actionPlanLoginPhone', phoneNumber)
    }

    return normalized
  } catch (error) {
    console.log(error, 'Error Occured')
  }
}

const ActionPlan = () => {
  const { darkMode } = useThemeMode()
  const [customers, setCustomers] = useState([])
  const [customersLoading, setCustomersLoading] = useState(true)
  const [customersError, setCustomersError] = useState('')
  const [customerRetryKey, setCustomerRetryKey] = useState(0)
  const [searchQuery, setSearchQuery] = useState('')
  const [showResults, setShowResults] = useState(false)
  const [phoneNumber, setPhoneNumber] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const navigate = useNavigate()
  const logoSrc = darkMode ? `${env.SVGS}/newlogo.svg` : `${env.SVGS}/newlogo2.svg`

  useEffect(() => {
    let cancelled = false

    const fetchCustomers = async () => {
      setCustomersLoading(true)
      setCustomersError('')
      try {
        const response = await get(`${env.API_URL}customerservice/api/RMCustomer`)
        if (
          !response ||
          response.isError ||
          String(response.code) !== '200' ||
          !Array.isArray(response.customers)
        ) {
          throw new Error('Unable to load clients. Try again or enter a phone number below.')
        }
        if (!cancelled) setCustomers(response.customers)
      } catch (error) {
        if (!cancelled) {
          setCustomers([])
          setCustomersError('Unable to load clients. Try again or enter a phone number below.')
        }
      } finally {
        if (!cancelled) setCustomersLoading(false)
      }
    }

    fetchCustomers()
    return () => {
      cancelled = true
    }
  }, [customerRetryKey])

  const filteredCustomers = useMemo(() => {
    const query = searchQuery.trim().toLowerCase().replace(/\s+/g, ' ')
    if (!query) return []
    const phoneQuery = query.replace(/[^\d]/g, '')
    return customers.filter((customer) => {
      const name = [customer.firstName, customer.lastName]
        .filter(Boolean)
        .join(' ')
        .replace(/\s+/g, ' ')
        .trim()
        .toLowerCase()
      return (
        name.includes(query) ||
        String(customer.clientCode || '')
          .toLowerCase()
          .includes(query) ||
        (/^[+\d\s()-]+$/.test(query) &&
          phoneQuery.length > 0 &&
          String(customer.phoneNumber || '')
            .replace(/\D/g, '')
            .includes(phoneQuery))
      )
    })
  }, [customers, searchQuery])

  const handleSelectCustomer = (customer) => {
    setPhoneNumber(customer.phoneNumber || '')
    setSearchQuery(
      [customer.firstName, customer.lastName]
        .filter(Boolean)
        .join(' ')
        .replace(/\s+/g, ' ')
        .trim() ||
        customer.clientCode ||
        '',
    )
    setShowResults(false)
  }

  const handlePhoneChange = (value) => {
    setPhoneNumber(value ?? '')
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    const normalizedPhone = normalizePhoneNumber(phoneNumber.trim())
    if (!normalizedPhone || normalizedPhone.replace(/\D/g, '').length < 10) {
      toast.error('Client phone number is required')
      return
    }

    setIsSubmitting(true)
    try {
      const res = await fetchCustomerDetails(normalizedPhone)

      if (res?.code === '200' && res?.status === 'SUCCESS' && res?.data) {
        toast.success('Login successful')
        navigate(ADMIN_ADD_PLAN_PATH, {
          state: {
            data: res.data,
          },
        })
      } else {
        toast.error('Login unsuccessful')
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div
      className={cn(
        'relative flex min-h-[100svh] w-full items-center justify-center overflow-hidden px-4 py-10',
        darkMode ? 'bg-[#040313]' : 'bg-white',
      )}
    >
      <div
        aria-hidden='true'
        className={cn(
          'pointer-events-none absolute inset-0 z-0',
          darkMode ? 'opacity-70' : 'opacity-100',
        )}
        style={darkMode ? darkGridStyle : lightGridStyle}
      />

      <div className='relative z-10 mx-auto flex w-full max-w-[360px] min-w-0 flex-col items-center lg:max-w-[432px]'>
        <img src={logoSrc} alt='Fabits' className='mb-8 h-8 w-auto object-contain' />

        <div className='mb-8 flex w-full flex-col gap-y-1'>
          <h1
            className={cn(
              'text-center font-IBM text-[28px] font-bold leading-9 lg:text-[36px] lg:leading-10',
              darkMode ? 'text-white' : 'text-black-950',
            )}
          >
            Admin Access
          </h1>
          <p
            className={cn(
              'text-center font-IBM text-sm font-medium leading-5',
              darkMode ? 'text-black-300' : 'text-black-400',
            )}
          >
            Sign in with client details to continue
          </p>
        </div>

        <form onSubmit={handleSubmit} className='flex w-full min-w-0 flex-col gap-y-5 font-IBM'>
          <div className='flex flex-col gap-2'>
            <AuthFloatingInput
              id='clientSearch'
              label='Search clients'
              value={searchQuery}
              onChange={(event) => {
                setSearchQuery(event.target.value)
                setShowResults(true)
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') event.preventDefault()
                if (event.key === 'Escape') setShowResults(false)
              }}
              darkSurface={darkMode}
              autoComplete='off'
              aria-describedby='client-search-status'
            />
            <p id='client-search-status' role='status' className='text-xs text-black-400'>
              {customersLoading
                ? 'Loading clients...'
                : customersError || 'Search by name, phone number, or client code.'}
            </p>
            {customersError && (
              <Button
                type='button'
                variant='outline'
                onClick={() => setCustomerRetryKey((key) => key + 1)}
              >
                Retry loading clients
              </Button>
            )}
            {showResults && searchQuery.trim() && !customersLoading && !customersError && (
              <div
                className={cn(
                  'max-h-60 overflow-y-auto rounded-xl border',
                  darkMode
                    ? 'border-[#3a3d56] bg-[#040313] text-white'
                    : 'border-[#D3DBEC] bg-white text-black-950',
                )}
              >
                {filteredCustomers.length === 0 ? (
                  <p className='px-3 py-4 text-sm' role='status'>
                    No clients found.
                  </p>
                ) : (
                  <ul aria-label='Matching clients'>
                    {filteredCustomers.map((customer, index) => (
                      <li key={`${customer.clientCode}-${customer.phoneNumber}-${index}`}>
                        <button
                          type='button'
                          disabled={!customer.phoneNumber}
                          onClick={() => handleSelectCustomer(customer)}
                          className='flex w-full flex-col gap-1 px-3 py-3 text-left hover:bg-primary-500/10 focus-visible:bg-primary-500/10 focus-visible:outline-none disabled:opacity-50'
                        >
                          <span className='text-sm font-semibold'>
                            {[customer.firstName, customer.lastName]
                              .filter(Boolean)
                              .join(' ')
                              .trim() || 'Unnamed client'}
                          </span>
                          <span className='text-xs text-black-400'>
                            {customer.phoneNumber || 'No phone number'} ·{' '}
                            {customer.clientCode?.toUpperCase() || '—'}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>

          <AuthFloatingPhoneInput
            label='Client Phone Number'
            phoneNumber={phoneNumber}
            setPhoneNumber={handlePhoneChange}
            darkSurface={darkMode}
          />

          <Button
            type='submit'
            disabled={isSubmitting}
            className='login-btn mt-1 h-11 w-full rounded-xl bg-primary-500 text-sm font-semibold text-white shadow-[0_10px_24px_rgba(40,24,255,0.18)] hover:bg-[#2818ff] disabled:bg-[#97aaff] disabled:opacity-100'
          >
            {isSubmitting ? 'Submitting...' : 'Continue'}
          </Button>
        </form>
      </div>
    </div>
  )
}

export default ActionPlan
